import { PageLoader } from '@/components/feedback/PageLoader';
import { SystemNotificationBanner } from '@/components/notifications/SystemNotificationBanner';
import { CompanyVisibilityAlert } from '@/components/notifications/CompanyVisibilityAlert';
import { useAuth } from '@/hooks/use-auth';
import { useCompany } from '@/hooks/use-company';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSIONS } from '@/lib/permissions';
import { resolvePrivateLayoutRedirect } from '@/lib/route-guards';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useEffect } from 'react';
import { AppSidebar } from './AppSidebar';
import { AppTopbar } from './AppTopbar';

export function AppLayout() {
  const location = useLocation();
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const { activeCompany, isLoading: isCompanyLoading } = useCompany();
  const { can } = usePermissions();
  const canFinishOnboarding = can(PERMISSIONS.COMPANY_ONBOARDING_FINISH);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('#main-content')?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  if (isAuthLoading || isCompanyLoading) {
    return <PageLoader />;
  }

  const redirectTo = resolvePrivateLayoutRedirect({
    isAuthenticated,
    user,
    activeCompany,
    canFinishOnboarding,
    pathname: location.pathname,
  });
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="bg-background min-h-dvh">
      <a
        href="#main-content"
        className="bg-background text-foreground focus:ring-ring fixed top-3 left-3 z-50 -translate-y-24 rounded-md px-4 py-3 font-medium shadow-lg transition-transform focus:translate-y-0 focus:ring-2 focus:outline-none"
      >
        Pular para o conteúdo principal
      </a>
      <AppSidebar />

      <div className="min-h-dvh lg:pl-80">
        <AppTopbar />

        <CompanyVisibilityAlert />
        <SystemNotificationBanner />

        <main id="main-content" tabIndex={-1} className="focus:outline-none">
          <div className="animate-fade-in p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
